import { IonSpinner } from '@ionic/react';
import { useMemo, useState } from 'react';

import type { CodigoServico } from '~/api/clients';
import {
  AVISO_ALIQUOTA_PADRAO,
  isAliquotaPadrao,
  parseAliquota,
  useCodigosServicoQuery,
} from '~/api/hooks/useCodigosServico';
import { FieldWrap, TextAreaField } from '~/components/forms/FormField';
import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { useWizard } from '~/pages/emitir/wizardContext';
import { type ServicoInput, type ValorInput, servicoSchema } from '~/schemas/invoice';

interface StepServicoProps {
  fieldErrors: Record<string, string>;
}

export function StepServico({ fieldErrors }: StepServicoProps): JSX.Element {
  const { draft, setServico, setValor } = useWizard();
  const { servico } = draft;
  const [search, setSearch] = useState('');

  const codigosQuery = useCodigosServicoQuery(draft.clientId);

  const filteredCodigos = useMemo(() => {
    const codigos = codigosQuery.data?.codigos ?? [];
    if (!search.trim()) return codigos.slice(0, 50);
    const normalized = search.trim().toLowerCase();
    return codigos
      .filter(
        (codigo) =>
          codigo.codigo.toLowerCase().includes(normalized) ||
          codigo.descricao.toLowerCase().includes(normalized),
      )
      .slice(0, 50);
  }, [codigosQuery.data, search]);

  const selectedCodigo = useMemo(
    () => codigosQuery.data?.codigos.find((codigo) => codigo.codigo === servico.codigo_servico),
    [codigosQuery.data, servico.codigo_servico],
  );

  const handleSelect = (codigo: CodigoServico): void => {
    setServico({ ...servico, codigo_servico: codigo.codigo });
    const aliquota = parseAliquota(codigo.aliquota_iss);
    if (aliquota !== undefined) {
      setValor((prev: ValorInput) => ({ ...prev, iss_aliquota: aliquota }));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionHead
        label="Código do serviço"
        action={
          codigosQuery.data ? (
            <span className="rx-mono">Tabela {codigosQuery.data.tabela ?? '—'}</span>
          ) : null
        }
      />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Search box — pílula institucional com hairline */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'var(--rx-surface-alt)',
            borderRadius: 10,
          }}
        >
          <Icon name="search" size={14} color="var(--rx-text-3)" />
          <input
            id="codigo-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar código ou descrição"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: 14,
              color: 'var(--rx-text)',
              letterSpacing: '-0.1px',
            }}
          />
        </div>

        {fieldErrors.codigo_servico && (
          <p
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--rx-negative)',
            }}
          >
            <Icon name="warn" size={13} />
            {fieldErrors.codigo_servico}
          </p>
        )}

        <div
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: 10,
            background: 'var(--rx-surface)',
            border: '0.5px solid var(--rx-border)',
          }}
        >
          {codigosQuery.isPending ? (
            <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
              <IonSpinner name="dots" />
            </div>
          ) : codigosQuery.isError ? (
            <div
              style={{
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--rx-negative)',
                fontSize: 13,
              }}
            >
              <Icon name="warn" size={14} />
              Não foi possível carregar os códigos. Tente recarregar.
            </div>
          ) : filteredCodigos.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--rx-text-2)',
              }}
            >
              Nenhum código encontrado.
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {filteredCodigos.map((codigo, idx) => {
                const isSelected = codigo.codigo === servico.codigo_servico;
                return (
                  <li key={codigo.codigo}>
                    <button
                      type="button"
                      onClick={() => handleSelect(codigo)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px 14px',
                        background: isSelected ? 'var(--rx-brand-tint)' : 'transparent',
                        color: isSelected ? 'var(--rx-brand)' : 'var(--rx-text)',
                        border: 'none',
                        borderTop: idx === 0 ? 'none' : '0.5px solid var(--rx-border)',
                        cursor: 'pointer',
                        display: 'block',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <span className="rx-mono" style={{ fontSize: 12, fontWeight: 600 }}>
                          {codigo.codigo}
                        </span>
                        <span
                          className="rx-mono"
                          style={{
                            fontSize: 10,
                            fontWeight: 500,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            color: isSelected ? 'var(--rx-brand)' : 'var(--rx-text-3)',
                          }}
                        >
                          ISS {codigo.aliquota_iss}%{isAliquotaPadrao(codigo) ? ' · padrão' : ''}
                        </span>
                      </div>
                      <p
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'inherit',
                        }}
                      >
                        {codigo.descricao}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {servico.codigo_servico && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              padding: '6px 10px',
              borderRadius: 999,
              background: 'var(--rx-brand-tint)',
              color: 'var(--rx-brand)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
            }}
          >
            <Icon name="check" size={11} />
            Selecionado · {servico.codigo_servico}
          </div>
        )}

        {selectedCodigo && isAliquotaPadrao(selectedCodigo) && (
          <p
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              fontSize: 12,
              lineHeight: 1.4,
              color: 'var(--rx-warning, #b45309)',
            }}
          >
            <Icon name="warn" size={13} />
            {AVISO_ALIQUOTA_PADRAO}
          </p>
        )}
      </div>

      <SectionHead label="Descrição do serviço" />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
        }}
      >
        <FieldWrap
          id="discriminacao"
          label="O que foi prestado?"
          error={fieldErrors.discriminacao}
          hint="Aparece literalmente na nota fiscal."
        >
          <TextAreaField
            id="discriminacao"
            value={servico.discriminacao}
            onChange={(value) => setServico({ ...servico, discriminacao: value })}
            placeholder="Ex.: Consultoria em desenvolvimento de software no mês de outubro/2026."
            maxLength={2000}
            rows={5}
          />
        </FieldWrap>
      </div>
    </div>
  );
}

export function validateServico(value: ServicoInput): Record<string, string> {
  const result = servicoSchema.safeParse(value);
  if (result.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !errors[key]) {
      errors[key] = issue.message;
    }
  }
  return errors;
}
